import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { INTL_DAY_MONTH_NAME_SHORT, INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Label from 'LuxKit/Label/Label'
import TextLink from 'LuxKit/TextLink'
import IconButton from 'LuxKit/Button/IconButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import { useAirlineByCode, useFlightDetailsForItems } from 'tripPlanner/hooks/api/flights'
import { isLeFlightSavedItemData, type FlightSavedItemData, type TransportOption } from 'tripPlanner/api/types'
import { didClickInteractableElement } from 'components/Common/NestableClickable/NestableClickable'
import useViewBookedShortlistedFlight from './ShortlistedFlightsSection/useViewBookedShortlistedFlight'
import {
  Tile,
  HeaderRow,
  HeaderChevron,
  HeaderLogo,
  CarrierLogoFrame,
  CarrierLogoImage,
  HeaderText,
  HeaderSummary,
  HeaderLabels,
  HeaderTrailing,
  MetaRow,
  MetaItem,
  ChevronButton,
  ExpandedBody,
  LegBlock,
  LegCard,
  LegPlainRow,
  SavedOptionGroupCard,
  LegRoute,
  LegDateTime,
  LegFlightNumber,
  LayoverRow,
  TileFooter,
  ViewBookingFooter,
  SavedOptionsToggle,
  SavedOptionsList,
  SavedOptionRow,
} from './BookedFlightTile.styles'
import {
  getFlightSavedData,
  formatTimeOnly,
  getDateParts,
  getPreferredDateParts,
  getSavedFlightBoundaryDateParts,
  dayOffset,
  formatFlightTimeRange,
  getLayoverLabel,
  getHydratedLeg,
  getSegmentLayoverLabel,
  legStops,
  getStopsLabel,
} from './bookedFlightTileUtils'

function CarrierLogo({ data, size = 40, bordered = true }: { data: FlightSavedItemData | undefined; size?: number; bordered?: boolean }) {
  const { data: airline } = useAirlineByCode({ code: data?.airlineCode })
  const logoSrc = data?.carrierLogo || airline?.airlineBrandMark || airline?.airlineLogo
  const alt = data?.airline ?? airline?.airlineName ?? ''
  const imageSize = bordered ? size - 12 : size

  return (
    <CarrierLogoFrame $size={size} $bordered={bordered}>
      {logoSrc
        ? <CarrierLogoImage $size={imageSize} src={logoSrc} alt={alt} />
        : <LinePlaneFlyIcon size="S" />}
    </CarrierLogoFrame>
  )
}

interface LegRowInput {
  logo: ReactNode
  route: string
  dateLabel?: string
  timeRange?: string
  flightLabel?: string
}

/** Presentational leg row content (logo · route · date/time · flight numbers) shared by all variants. */
function LegRowContent({ logo, route, dateLabel, timeRange, flightLabel }: LegRowInput) {
  return (
    <>
      {logo}
      <LegRoute>
        <BodyText variant="small" weight="bold">{route}</BodyText>
      </LegRoute>
      <LegDateTime>
        {dateLabel && <BodyText variant="small" colour="neutral-two">{dateLabel}</BodyText>}
        {timeRange && <BodyText variant="medium" weight="bold">{timeRange}</BodyText>}
      </LegDateTime>
      {flightLabel && (
        <LegFlightNumber>
          <BodyText variant="small" colour="neutral-two">{flightLabel}</BodyText>
        </LegFlightNumber>
      )}
    </>
  )
}

function useWeekdayDate(value: string | undefined): string | undefined {
  const intl = useIntl()
  return useMemo(() => {
    const parts = getDateParts(value)
    if (!parts) return undefined
    return intlDateFormatter(new Date(parts.year, parts.month - 1, parts.day), INTL_SHORT_DAY_NAME_DAY_MONTH, intl.locale)
  }, [intl.locale, value])
}

function usePreferredWeekdayDate(date: string | undefined, fallbackDateTime: string | undefined): string | undefined {
  const intl = useIntl()
  return useMemo(() => {
    const parts = getPreferredDateParts(date, fallbackDateTime)
    if (!parts) return undefined
    return intlDateFormatter(new Date(parts.year, parts.month - 1, parts.day), INTL_SHORT_DAY_NAME_DAY_MONTH, intl.locale)
  }, [date, fallbackDateTime, intl.locale])
}

function formatShortDate(intl: ReturnType<typeof useIntl>, parts: { year: number; month: number; day: number } | undefined): string | undefined {
  if (!parts) return undefined
  return intlDateFormatter(new Date(parts.year, parts.month - 1, parts.day), INTL_DAY_MONTH_NAME_SHORT, intl.locale)
}

function useSavedFlightBoundaryDate(data: FlightSavedItemData | undefined, boundary: 'departure' | 'arrival'): string | undefined {
  const intl = useIntl()
  return useMemo(() => formatShortDate(intl, getSavedFlightBoundaryDateParts(data, boundary)), [boundary, data, intl])
}

function useSavedFlightLegDate(data: FlightSavedItemData | undefined, boundary: 'departure' | 'arrival' | 'range'): string | undefined {
  const intl = useIntl()
  return useMemo(() => {
    if (boundary !== 'range') return formatShortDate(intl, getSavedFlightBoundaryDateParts(data, boundary))
    const departureDate = formatShortDate(intl, getSavedFlightBoundaryDateParts(data, 'departure'))
    const arrivalDate = formatShortDate(intl, getSavedFlightBoundaryDateParts(data, 'arrival'))
    if (departureDate && arrivalDate && departureDate !== arrivalDate) return `${departureDate} - ${arrivalDate}`
    return departureDate ?? arrivalDate
  }, [boundary, data, intl])
}

function getSavedFlightLegBoundary(data: FlightSavedItemData, isReturnLeg: boolean): 'departure' | 'arrival' | 'range' {
  if (data.flightGroupType === 'multi_city') return 'range'
  return isReturnLeg ? 'departure' : 'arrival'
}

/** Leg row built from saved item data (used as the booked-tile fallback before the order hydrates). */
function SavedLegRow({ data }: { data: FlightSavedItemData }) {
  const intl = useIntl()
  const dateLabel = usePreferredWeekdayDate(data.departureDate, data.departureTime)
  const route = `${data.departureAirport ?? ''} > ${data.arrivalAirport ?? ''}`.trim()
  const timeRange = formatFlightTimeRange(data)
  const flightLabel = [data.flightNumber, data.airline ? `(${data.airline})` : undefined].filter(Boolean).join(' ')

  return (
    <LegBlock>
      <LegCard>
        <LegRowContent
          logo={<CarrierLogo data={data} size={40} bordered={false} />}
          route={route}
          dateLabel={dateLabel}
          timeRange={timeRange}
          flightLabel={flightLabel}
        />
      </LegCard>
      {data.layovers?.map((layover, index) => (
        <LayoverRow key={`${layover.airportCode ?? index}-${index}`}>
          <LineClockIcon size="XS" />
          <Caption variant="medium" colour="neutral-two">{getLayoverLabel(intl, layover)}</Caption>
        </LayoverRow>
      ))}
    </LegBlock>
  )
}

/** Leg row built from a hydrated booking journey leg — one card for the whole journey
 * (overall route + combined times + joined flight numbers) with layover sub-lines underneath. */
function HydratedLegRow({ leg }: { leg: App.Leg }) {
  const intl = useIntl()
  const flights = leg.flights ?? []
  const firstFlight = flights[0]
  const lastFlight = flights[flights.length - 1]
  const dateLabel = useWeekdayDate(firstFlight?.departingDate)

  const route = `${leg.departureAirport ?? firstFlight?.departureAirport ?? ''} > ${leg.arrivalAirport ?? lastFlight?.arrivalAirport ?? ''}`.trim()
  const depTime = formatTimeOnly(firstFlight?.departingTime)
  const arrTime = formatTimeOnly(lastFlight?.arrivalTime)
  const offset = dayOffset(firstFlight?.departingDate, lastFlight?.arrivalDate)
  const timeRange = depTime && arrTime ? `${depTime} - ${arrTime}${offset > 0 ? `+${offset}` : ''}` : undefined
  const airlineName = firstFlight?.marketingAirline?.name
  const flightNumbers = flights.map(f => f.flightNumber).filter(Boolean).join(' / ')
  const flightLabel = [flightNumbers, airlineName ? `(${airlineName})` : undefined].filter(Boolean).join(' ')
  // Tail/brand mark (matches the header logo) rather than the wide airline logo.
  const logoSrc = firstFlight?.marketingAirline?.brandMarkUrl || firstFlight?.marketingAirline?.logoUrl

  return (
    <LegBlock>
      <LegCard>
        <LegRowContent
          logo={(
            <CarrierLogoFrame $size={40} $bordered={false}>
              {logoSrc ? <CarrierLogoImage $size={40} src={logoSrc} alt={airlineName ?? ''} /> : <LinePlaneFlyIcon size="S" />}
            </CarrierLogoFrame>
          )}
          route={route}
          dateLabel={dateLabel}
          timeRange={timeRange}
          flightLabel={flightLabel}
        />
      </LegCard>
      {flights.slice(0, -1).map((flight, index) => (
        <LayoverRow key={flight.flightUniqueId ?? `${flight.flightNumber}-${index}`}>
          <LineClockIcon size="XS" />
          <Caption variant="medium" colour="neutral-two">
            {getSegmentLayoverLabel(intl, flight, flights[index + 1])}
          </Caption>
        </LayoverRow>
      ))}
    </LegBlock>
  )
}

/** Compact saved-option leg: logo · route, with "date · time · stops" underneath (no flight number). */
function SavedOptionLeg({ data, bordered = true, isReturnLeg = false }: { data: FlightSavedItemData; bordered?: boolean; isReturnLeg?: boolean }) {
  const intl = useIntl()
  const dateLabel = useSavedFlightLegDate(data, getSavedFlightLegBoundary(data, isReturnLeg))
  const route = `${data.departureAirport ?? ''} → ${data.arrivalAirport ?? ''}`.trim()
  const timeRange = formatFlightTimeRange(data)
  const stopsLabel = getStopsLabel(intl, data.layovers?.length ?? 0)
  const metaSegments = [dateLabel, timeRange, stopsLabel].filter(Boolean) as Array<string>
  const Container = bordered ? LegCard : LegPlainRow

  return (
    <Container>
      <CarrierLogo data={data} size={32} />
      <HeaderText>
        <BodyText variant="medium" weight="bold">{route}</BodyText>
        <MetaRow>
          {metaSegments.map(segment => (
            <MetaItem key={segment}>
              <BodyText variant="small" colour="neutral-two">{segment}</BodyText>
            </MetaItem>
          ))}
        </MetaRow>
      </HeaderText>
    </Container>
  )
}

interface SavedOptionRowItemProps {
  option: TransportOption
  onClick: () => void
}

function SavedOptionRowItem({ option, onClick }: SavedOptionRowItemProps) {
  const savedData = getFlightSavedData(option)
  const returnData = option.returnLeg ? getFlightSavedData(option.returnLeg) : undefined
  if (!savedData) return null

  // Group the outbound + return legs into one bordered card so a return reads as a single itinerary.
  if (returnData) {
    return (
      <SavedOptionRow type="button" onClick={onClick}>
        <SavedOptionGroupCard>
          <SavedOptionLeg data={savedData} bordered={false} />
          <SavedOptionLeg data={returnData} bordered={false} isReturnLeg />
        </SavedOptionGroupCard>
      </SavedOptionRow>
    )
  }

  return (
    <SavedOptionRow type="button" onClick={onClick}>
      <SavedOptionLeg data={savedData} />
    </SavedOptionRow>
  )
}

interface Props {
  /** The booked flight transport option. */
  option: TransportOption
  /** Other saved (non-booked) flight options on this leg. */
  otherSavedOptions: Array<TransportOption>
  onOpenDrawer: () => void
  onDelete: () => void
  /** Show the return leg as the header summary (for the reversed tile at the end of the itinerary). */
  reversed?: boolean
}

function BookedFlightTile({ option, otherSavedOptions, onOpenDrawer, onDelete, reversed = false }: Props) {
  const intl = useIntl()
  const { viewBookedFlightDetails, viewUnbookedFlightDetails, flightDetailsDrawer } = useViewBookedShortlistedFlight()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isSavedOptionsExpanded, setIsSavedOptionsExpanded] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuTriggerRef = useRef<HTMLButtonElement>(null)

  const savedData = getFlightSavedData(option)
  const returnData = option.returnLeg ? getFlightSavedData(option.returnLeg) : undefined
  const isReturn = Boolean(returnData)
  const otherSavedCount = otherSavedOptions.length
  // Saved options live on the outbound tile only; the reversed return tile at the end of
  // the itinerary never lists them to avoid duplication.
  const showSavedOptions = otherSavedCount > 0 && !reversed

  // Hydrate the booked order so the expanded view can show each flight segment with
  // layovers between them (saved data only has overall leg routes + a layover list).
  const leOutbound = isLeFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  const leReturn = isLeFlightSavedItemData(option.returnLeg?.savedItemData) ? option.returnLeg.savedItemData : undefined
  const bookedOrderId = leOutbound?.orderId ?? leReturn?.orderId ?? ''
  const bookedItemIds = useMemo(
    () => [leOutbound?.orderFlightItemId, leReturn?.orderFlightItemId]
      .filter((id, index, ids): id is string => !!id && ids.indexOf(id) === index),
    [leOutbound?.orderFlightItemId, leReturn?.orderFlightItemId],
  )
  // Fetched eagerly (not gated on expansion) so the collapsed header can show the
  // real stop count — saved data alone doesn't carry the return leg's layovers.
  const { data: bookedDetails } = useFlightDetailsForItems({
    orderId: bookedOrderId,
    itemIds: bookedItemIds,
    enabled: bookedOrderId.length > 0 && bookedItemIds.length > 0,
  })

  const outboundLeg = useMemo(
    () => getHydratedLeg(bookedDetails, bookedItemIds, leOutbound?.orderFlightItemId, leOutbound?.legOrder, 0),
    [bookedDetails, bookedItemIds, leOutbound?.orderFlightItemId, leOutbound?.legOrder],
  )
  const returnLeg = useMemo(
    () => (leReturn
      ? getHydratedLeg(bookedDetails, bookedItemIds, leReturn.orderFlightItemId, leReturn.legOrder, leOutbound?.orderFlightItemId === leReturn.orderFlightItemId ? 1 : 0)
      : undefined),
    [bookedDetails, bookedItemIds, leReturn, leOutbound?.orderFlightItemId],
  )

  const bookingUrl = bookedOrderId ? `/account/my-escapes/order/${bookedOrderId}` : undefined

  // The reversed tile (rendered at the end of the itinerary) summarises the return leg
  // in its header, while the expanded dropdown stays identical to the outbound tile.
  const headerLeg = reversed ? (returnData ?? savedData) : savedData

  const route = headerLeg
    ? `${headerLeg.departureAirport ?? ''} → ${headerLeg.arrivalAirport ?? ''}`.trim()
    : ''

  const dateLabel = useSavedFlightBoundaryDate(headerLeg, reversed ? 'departure' : 'arrival')

  const timeRangeLabel = formatFlightTimeRange(headerLeg)
  // Stop count for the leg the header is summarising: the return leg on the reversed
  // tile, otherwise the outbound leg. Summing both legs would let a reversed tile show
  // "1 stop" for a direct return just because the outbound leg had a stop.
  const headerStops = useMemo(
    () => (reversed ? legStops(returnLeg, returnData) : legStops(outboundLeg, savedData)),
    [reversed, outboundLeg, returnLeg, savedData, returnData],
  )
  const stopsLabel = getStopsLabel(intl, headerStops)
  const metaSegments = useMemo(
    () => [dateLabel, timeRangeLabel, stopsLabel].filter(Boolean) as Array<string>,
    [dateLabel, timeRangeLabel, stopsLabel],
  )

  const arrivalLeg = returnData ?? savedData
  const arrivalDateLabel = useMemo(() => {
    const parts = getPreferredDateParts(arrivalLeg?.arrivalDate, arrivalLeg?.arrivalTime)
    if (!parts) return undefined
    return intlDateFormatter(new Date(parts.year, parts.month - 1, parts.day), INTL_DAY_MONTH_NAME_SHORT, intl.locale)
  }, [intl.locale, arrivalLeg?.arrivalDate, arrivalLeg?.arrivalTime])

  const footerNote = useMemo(() => {
    const parts: Array<string> = []
    if (savedData?.airline) parts.push(savedData.airline)
    if (arrivalLeg?.arrivalAirport && arrivalDateLabel) {
      parts.push(intl.formatMessage({
        id: 'JHvN3Kzae1HC3dPWcbhl',
        defaultMessage: 'Arrives {airport} {date}',
        description: 'Arrival airport and date note on an expanded booked flight in trip planner',
      }, { airport: arrivalLeg.arrivalAirport, date: arrivalDateLabel }))
    }
    return parts.join(' · ')
  }, [arrivalDateLabel, arrivalLeg?.arrivalAirport, intl, savedData?.airline])

  const ticketTypeLabel = intl.formatMessage(isReturn
    ? { id: 'bookedFlightTileReturnTicket', defaultMessage: 'Return ticket', description: 'Pill label for a return booked flight on the trip planner transport tile' }
    : { id: 'bookedFlightTileOneWayTicket', defaultMessage: 'One-way ticket', description: 'Pill label for a one-way booked flight on the trip planner transport tile' })

  const handleOpenDetails = useCallback(() => {
    // Prefer the booked order details drawer; fall back to the saved-journey drawer
    // for booked flights that aren't linked to an order (e.g. chat/custom flights).
    const outboundLe = isLeFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
    const returnLe = isLeFlightSavedItemData(option.returnLeg?.savedItemData) ? option.returnLeg.savedItemData : undefined
    const orderId = outboundLe?.orderId ?? returnLe?.orderId
    const orderFlightItemId = outboundLe?.orderFlightItemId ?? returnLe?.orderFlightItemId

    if (orderId && orderFlightItemId) {
      viewBookedFlightDetails(option)
      return
    }

    viewUnbookedFlightDetails(option).catch(() => undefined)
  }, [viewBookedFlightDetails, viewUnbookedFlightDetails, option])

  // Clicking the tile opens the details drawer, except when the click landed on a nested
  // interactive element (chevron, kebab menu, View booking link), which handle themselves.
  // Note: check `Element` (not `HTMLElement`) so clicks on SVG icons inside those buttons
  // are still walked up to their interactive ancestor.
  const isInteractiveTarget = useCallback((event: React.SyntheticEvent) =>
    event.target instanceof Element && didClickInteractableElement(event.target as HTMLElement, event.currentTarget as HTMLElement),
  [])

  const handleTileClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (isInteractiveTarget(event)) return
    handleOpenDetails()
  }, [isInteractiveTarget, handleOpenDetails])

  const handleTileKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    if (isInteractiveTarget(event)) return
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      handleOpenDetails()
    }
  }, [isInteractiveTarget, handleOpenDetails])

  const toggleExpanded = useCallback(() => setIsExpanded(prev => !prev), [])
  const toggleSavedOptions = useCallback(() => setIsSavedOptionsExpanded(prev => !prev), [])
  const closeMenu = useCallback(() => setIsMenuOpen(false), [])
  const toggleMenu = useCallback(() => setIsMenuOpen(prev => !prev), [])

  const handleChangeTransport = useCallback(() => {
    closeMenu()
    onOpenDrawer()
  }, [closeMenu, onOpenDrawer])

  const handleDelete = useCallback(() => {
    closeMenu()
    onDelete()
  }, [closeMenu, onDelete])

  const savedOptionsToggleLabel = isSavedOptionsExpanded
    ? intl.formatMessage({
        id: 'LRwy8Sc5SD4f3DhDSi5_',
        defaultMessage: 'Hide saved options',
        description: 'Toggle link below a booked flight tile to hide other saved options in trip planner',
      })
    : intl.formatMessage({
        id: 'ud6YL6aImH3On2mLF6JR',
        defaultMessage: 'View {count, plural, one {# other saved option} other {# other saved options}}',
        description: 'Toggle link below a booked flight tile to view other saved options in trip planner',
      }, { count: otherSavedCount })

  const viewBookingLabel = intl.formatMessage({
    id: '9WSwDU_NfnpLL_2yDqiJ',
    defaultMessage: 'View booking',
    description: 'Link at the bottom of a booked flight tile that opens the booking in My Escapes',
  })

  const expandToggleLabel = isExpanded
    ? intl.formatMessage({
        id: 'yL4aS2gY--PA54w2tW1j',
        defaultMessage: 'Collapse flight details',
        description: 'Accessible label for the button that collapses the booked flight details in trip planner',
      })
    : intl.formatMessage({
        id: 'ewwu9OMEASSpYp51pj80',
        defaultMessage: 'Expand flight details',
        description: 'Accessible label for the button that expands the booked flight details in trip planner',
      })

  return (
    <Group direction="vertical" gap={0} fullWidth>
      <Tile role="button" tabIndex={0} onClick={handleTileClick} onKeyDown={handleTileKeyDown}>
        <HeaderRow>
          <ChevronButton type="button" aria-expanded={isExpanded} aria-label={expandToggleLabel} onClick={toggleExpanded}>
            <HeaderChevron $expanded={isExpanded} aria-hidden>
              <LineAngleDownIcon />
            </HeaderChevron>
          </ChevronButton>
          <HeaderLogo>
            <CarrierLogo data={headerLeg} />
          </HeaderLogo>
          <HeaderSummary>
            <BodyText variant="small" weight="bold">{route}</BodyText>
            <MetaRow>
              {metaSegments.map(segment => (
                <MetaItem key={segment}>
                  <BodyText variant="small" colour="neutral-two">{segment}</BodyText>
                </MetaItem>
              ))}
            </MetaRow>
          </HeaderSummary>
          <HeaderLabels>
            <Label kind="tertiary" variant="ghost">{ticketTypeLabel}</Label>
            <BookedLabel />
          </HeaderLabels>
          <HeaderTrailing>
            <IconButton kind="tertiary" size="small" ref={menuTriggerRef} onClick={toggleMenu}>
              <LineEllipsisVIcon />
            </IconButton>
            <DropdownList
              size="S"
              open={isMenuOpen}
              onClose={closeMenu}
              triggerRef={menuTriggerRef}
              anchorRef={menuTriggerRef}
              placement="bottom-end"
              portaled
            >
              <ClickableListItem
                title={intl.formatMessage({
                  id: '4-QHBS9UPMwRSIn5CgZd',
                  defaultMessage: 'Change transport',
                  description: 'Menu item to change the booked flight on the trip planner transport tile',
                })}
                size="medium"
                startIcon={<LineSearchIcon size="S" />}
                onClick={handleChangeTransport}
              />
              <ClickableListItem
                title={intl.formatMessage({
                  id: '7tkbQGnFzPWNKE1PfYFD',
                  defaultMessage: 'Delete',
                  description: 'Menu item to delete the booked flight on the trip planner transport tile',
                })}
                size="medium"
                startIcon={<LineTrashAltIcon size="S" />}
                onClick={handleDelete}
              />
            </DropdownList>
          </HeaderTrailing>
        </HeaderRow>

        {isExpanded && (
          <ExpandedBody>
            {outboundLeg
              ? <HydratedLegRow leg={outboundLeg} />
              : savedData && <SavedLegRow data={savedData} />}
            {returnLeg
              ? <HydratedLegRow leg={returnLeg} />
              : returnData && <SavedLegRow data={returnData} />}
            {(footerNote || bookingUrl) && (
              <TileFooter>
                {footerNote && (
                  <BodyText variant="small" colour="neutral-two">{footerNote}</BodyText>
                )}
                {bookingUrl && (
                  <ViewBookingFooter>
                    <TextLink
                      size="small"
                      weight="bold"
                      href={bookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {viewBookingLabel}
                    </TextLink>
                  </ViewBookingFooter>
                )}
              </TileFooter>
            )}
          </ExpandedBody>
        )}
      </Tile>

      {showSavedOptions && (
        <>
          <SavedOptionsToggle type="button" onClick={toggleSavedOptions}>
            {isSavedOptionsExpanded ? <LineAngleUpIcon size="XS" /> : <LineAngleDownIcon size="XS" />}
            <BodyText variant="small">{savedOptionsToggleLabel}</BodyText>
          </SavedOptionsToggle>

          {isSavedOptionsExpanded && (
            <SavedOptionsList>
              {otherSavedOptions.map(savedOption => (
                <SavedOptionRowItem
                  key={savedOption.itemId}
                  option={savedOption}
                  onClick={onOpenDrawer}
                />
              ))}
            </SavedOptionsList>
          )}
        </>
      )}
      {flightDetailsDrawer}
    </Group>
  )
}

export default BookedFlightTile
